import { useMemo, useState } from "react";
import {
    CheckCircle2,
    ClipboardCheck,
    Inbox,
    ShieldCheck,
    ShieldX,
    Workflow,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    type Approval,
    type ApprovalFilters,
    type ApprovalKind,
    type ApprovalStatus,
    useApprovalCounts,
    useApprovals,
    useApproval,
    useCancelApproval,
    useDecideApproval,
} from "~/lib/approvals";
import { TableSkeleton } from "~/components/TableSkeleton";

const KIND_LABEL: Record<ApprovalKind, string> = {
    rule: "Detection rule",
    workflow: "Workflow",
    ioc_bulk: "IoC bulk op",
};

const STATUS_PILL: Record<ApprovalStatus, string> = {
    pending: "status-pill--queued",
    approved: "status-pill--live",
    rejected: "status-pill--failed",
    cancelled: "status-pill--queued",
};

function relative(iso?: string): string {
    if (!iso) return "—";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return "—";
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

export function ApprovalsPage(): React.ReactElement {
    const me = useCurrentUser();
    const counts = useApprovalCounts();
    const [filters, setFilters] = useState<ApprovalFilters>({ status: "pending" });
    const list = useApprovals(filters);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const detail = useApproval(selectedId);
    const decide = useDecideApproval();
    const cancel = useCancelApproval();

    const canDecide = useMemo(() => {
        const roles = me.data?.roles ?? [];
        return roles.includes("admin") || roles.includes("rule_approver") || roles.includes("workflow_approver");
    }, [me.data?.roles]);
    const myUserId = me.data?.id;

    async function doDecide(id: string, approve: boolean): Promise<void> {
        const note = window.prompt(
            approve ? "Optional note for this approval:" : "Reason for rejection (optional):",
        );
        if (note === null) return;
        try {
            await decide.mutateAsync({ id, approve, note });
            toast.success(approve ? "Approved" : "Rejected");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to record decision");
        }
    }

    async function doCancel(id: string): Promise<void> {
        if (!window.confirm("Cancel this pending approval?")) return;
        try {
            await cancel.mutateAsync({ id });
            toast.success("Cancelled");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to cancel");
        }
    }

    const rows = list.data ?? [];

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon">
                                <ClipboardCheck size={14} aria-hidden />
                            </span>
                            Governance
                        </span>
                        <h1 className="page-header__title">Approval queue</h1>
                        <p className="page-header__description">
                            Cross-resource inbox for pending changes — detection rules, workflows, and
                            large-scale IoC operations. Approvers act here; per-resource queues continue
                            to drive their own state machines.
                        </p>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item">
                        <span className="live-dot live-dot--success" aria-hidden />
                        {counts.data ? `${counts.data.pending} pending` : "—"}
                    </span>
                    {counts.data && counts.data.myPending > 0 && (
                        <span className="page-header__meta__item tabular-nums" style={{ color: "var(--color-accent)" }}>
                            {counts.data.myPending} need your vote
                        </span>
                    )}
                </div>
            </header>

            <section className="metric-grid" aria-label="Approval metrics">
                <div className="metric-card metric-card--accent">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><Inbox size={12} aria-hidden /></span>
                        Pending
                    </span>
                    <span className="metric-card__value">{counts.data?.pending ?? "—"}</span>
                    <span className="metric-card__sub">Awaiting at least one decision</span>
                </div>
                <div className="metric-card metric-card--success">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><CheckCircle2 size={12} aria-hidden /></span>
                        Approved · 24h
                    </span>
                    <span className="metric-card__value">{counts.data?.approved24h ?? "—"}</span>
                    <span className="metric-card__sub">Decided in the last day</span>
                </div>
                <div className="metric-card metric-card--danger">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><XCircle size={12} aria-hidden /></span>
                        Rejected · 24h
                    </span>
                    <span className="metric-card__value">{counts.data?.rejected24h ?? "—"}</span>
                    <span className="metric-card__sub">Decided in the last day</span>
                </div>
                <div className="metric-card">
                    <span className="metric-card__label">
                        <span className="metric-card__label__icon"><ShieldCheck size={12} aria-hidden /></span>
                        Your queue
                    </span>
                    <span className="metric-card__value">{counts.data?.myPending ?? "—"}</span>
                    <span className="metric-card__sub">Pending where you haven't voted</span>
                </div>
            </section>

            <section className="section-card" aria-label="Approval queue">
                <div className="section-card__header">
                    <div className="section-card__title-group">
                        <h2 className="section-card__title">Inbox</h2>
                        <p className="section-card__subtitle">
                            Click a row to inspect the diff and act on it.
                        </p>
                    </div>
                    <div className="section-card__actions toolbar">
                        <div className="cluster-xs" role="tablist" aria-label="Status filter">
                            {(["pending", "approved", "rejected", "cancelled", ""] as const).map((s) => (
                                <button
                                    key={s || "all"}
                                    type="button"
                                    role="tab"
                                    className={`chip ${filters.status === s ? "chip--accent" : ""}`}
                                    onClick={() => setFilters((f) => ({ ...f, status: s }))}
                                >
                                    {s || "all"}
                                </button>
                            ))}
                        </div>
                        <div className="cluster-xs" role="tablist" aria-label="Kind filter">
                            {(["", "rule", "workflow", "ioc_bulk"] as const).map((k) => (
                                <button
                                    key={k || "all"}
                                    type="button"
                                    role="tab"
                                    className={`chip ${filters.kind === k ? "chip--accent" : ""}`}
                                    onClick={() => setFilters((f) => ({ ...f, kind: k }))}
                                >
                                    {k ? KIND_LABEL[k as ApprovalKind] : "all kinds"}
                                </button>
                            ))}
                        </div>
                        <input
                            type="search"
                            placeholder="Search title / summary / id"
                            className="input"
                            value={filters.q ?? ""}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            style={{ minWidth: 220 }}
                        />
                    </div>
                </div>
                {list.isLoading ? (
                    <TableSkeleton rows={6} columns={6} />
                ) : list.isError ? (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load approvals</div>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="empty-state">
                        <Workflow size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Nothing to act on</div>
                        <div className="empty-state__body">No approvals match the current filters.</div>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table data-table--dense">
                            <thead>
                                <tr>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Kind</th>
                                    <th>Title</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Requested</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>By</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Votes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((a: Approval) => (
                                    <tr
                                        key={a.id}
                                        className={selectedId === a.id ? "is-selected" : undefined}
                                        onClick={() => setSelectedId(a.id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td><span className="chip">{KIND_LABEL[a.kind]}</span></td>
                                        <td style={{ maxWidth: 0, width: "100%" }}>
                                            <div className="stack-xs" style={{ minWidth: 0 }}>
                                                <span className="text-13" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.title}>
                                                    {a.title}
                                                </span>
                                                {a.summary && (
                                                    <span className="text-12 text-text-muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.summary}>
                                                        {a.summary}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-12 text-text-muted tabular-nums" style={{ whiteSpace: "nowrap" }}>
                                            {relative(a.requestedAt)}
                                        </td>
                                        <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                                            {a.requestedByEmail || a.requestedBy.slice(0, 8)}
                                        </td>
                                        <td><span className={`status-pill ${STATUS_PILL[a.status]}`}>{a.status}</span></td>
                                        <td className="text-12 tabular-nums" style={{ whiteSpace: "nowrap" }}>
                                            {a.approveCount}/{a.approversRequired}
                                            {a.rejectCount > 0 && <span style={{ color: "var(--color-danger)" }}> · {a.rejectCount} reject</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {selectedId && (
                <section className="section-card" aria-label="Approval detail">
                    <div className="section-card__header">
                        <div className="section-card__title-group">
                            <h2 className="section-card__title">{detail.data?.approval.title ?? "Loading…"}</h2>
                            {detail.data && (
                                <p className="section-card__subtitle">
                                    {KIND_LABEL[detail.data.approval.kind]} ·{" "}
                                    {detail.data.approval.resourceType}/{detail.data.approval.resourceId}
                                    {typeof detail.data.approval.version === "number" && (
                                        <> · v{detail.data.approval.version}</>
                                    )}
                                </p>
                            )}
                        </div>
                        <div className="section-card__actions cluster-xs">
                            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelectedId(null)}>
                                Close
                            </button>
                            {canDecide && detail.data?.approval.status === "pending" && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn--primary btn--sm press-crush"
                                        onClick={() => void doDecide(selectedId, true)}
                                        disabled={decide.isPending}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--ghost btn--sm press-crush"
                                        onClick={() => void doDecide(selectedId, false)}
                                        disabled={decide.isPending}
                                    >
                                        Reject
                                    </button>
                                </>
                            )}
                            {detail.data?.approval.status === "pending" &&
                                myUserId === detail.data.approval.requestedBy && (
                                <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => void doCancel(selectedId)}
                                    disabled={cancel.isPending}
                                >
                                    Cancel request
                                </button>
                            )}
                        </div>
                    </div>
                    {detail.isLoading ? (
                        <TableSkeleton rows={3} columns={2} />
                    ) : detail.isError || !detail.data ? (
                        <div className="empty-state">
                            <ShieldX size={20} className="empty-state__icon" aria-hidden />
                            <div className="empty-state__title">Could not load this approval</div>
                        </div>
                    ) : (
                        <div className="stack">
                            {detail.data.approval.summary && (
                                <p className="text-13" style={{ whiteSpace: "pre-wrap" }}>
                                    {detail.data.approval.summary}
                                </p>
                            )}
                            <div className="stack-xs">
                                <h3 className="type-h3">Payload</h3>
                                <pre
                                    className="type-mono-sm"
                                    style={{
                                        margin: 0,
                                        padding: "var(--space-3)",
                                        background: "var(--color-surface-2)",
                                        borderRadius: "var(--radius-sm)",
                                        maxHeight: 320,
                                        overflow: "auto",
                                    }}
                                >
                                    {JSON.stringify(detail.data.approval.payload, null, 2)}
                                </pre>
                            </div>
                            <div className="stack-xs">
                                <h3 className="type-h3">Decision history</h3>
                                {detail.data.decisions.length === 0 ? (
                                    <p className="text-12 text-text-muted">No decisions yet.</p>
                                ) : (
                                    <ul className="stack-xs" style={{ listStyle: "none", padding: 0 }}>
                                        {detail.data.decisions.map((d) => (
                                            <li key={d.id} className="cluster-xs text-13">
                                                <span className={`status-pill ${d.decision === "approve" ? "status-pill--live" : "status-pill--failed"}`}>
                                                    {d.decision}
                                                </span>
                                                <span>{d.approverEmail || d.approverUserId.slice(0, 8)}</span>
                                                <span className="text-12 text-text-muted">{relative(d.decidedAt)}</span>
                                                {d.note && <span className="text-12">— {d.note}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
